import React, { useEffect, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Button from 'views/Button.js';
import Request from 'files/Request.js';
import API from 'files/api.js';
import Captcha from 'files/Captcha.js';

// Discount-capture modal: trade a phone number (SMS) or email for 10% off the
// Base Network. The channel is chosen by API.features.discountCapture
// ('sms' | 'email'); the server creates a unique Shopify code and delivers it
// over that channel (which doubles as verification).
//
// Backend contract:
//   Request.post(utils, '/shop/', { type: 'create_and_send_starter_discount',
//     channel: 'sms', phone_number, captcha_token } | { ..., channel: 'email', email_address, captcha_token })
//
// captcha_token is a reCAPTCHA v3 token generated for the 'starter_discount'
// action, which the server verifies.
const OutreachCaptureModal = ({ open, onClose, onSubscribed, utils }) => {

    const isSms = (API.features && API.features.discountCapture) === 'sms';

    const [value, setValue] = useState('');
    const [status, setStatus] = useState('idle'); // idle | submitting | done | error
    const [error, setError] = useState(null);

    // start loading recaptcha as soon as the form is shown
    useEffect(() => {
        if(open) {
            Captcha.load().catch(() => {});
        }
    }, [open]);

    if(!open) {
        return null;
    }

    const validate = () => {
        if(isSms) {
            if((value || '').replace(/\D/g, '').length < 10) {
                return 'Please enter a valid phone number.';
            }
        } else if(!Utils.validateEmail(value)) {
            return 'Please enter a valid email address.';
        }
        return null;
    };

    const onSubmit = async (e) => {
        if(e) { e.preventDefault(); }
        if(status === 'submitting') {
            return;
        }
        const invalid = validate();
        if(invalid) {
            setError(invalid);
            setStatus('error');
            return;
        }
        setStatus('submitting');
        setError(null);
        try {
            const captchaToken = await Captcha.getToken('starter_discount');
            await Request.post(utils, '/shop/', isSms
                ? { type: 'create_and_send_starter_discount', channel: 'sms', phone_number: value, captcha_token: captchaToken }
                : { type: 'create_and_send_starter_discount', channel: 'email', email_address: value, captcha_token: captchaToken });
            setStatus('done');
            if(onSubscribed) { onSubscribed(); }
        } catch(err) {
            setError((err && err.message) || 'Something went wrong. Please try again.');
            setStatus('error');
        }
    };

    const fieldStyle = {
        fontFamily: Appearance.fonts.body,
        fontSize: 15,
        padding: '13px 16px',
        borderRadius: 12,
        border: `1px solid ${Appearance.colors.border()}`,
        backgroundColor: Appearance.colors.white,
        color: Appearance.colors.text(),
        outline: 'none',
        width: '100%',
    };

    // Channel-specific copy
    const copy = isSms ? {
        blurb: 'Drop your number and we\'ll text you a code for 10% off the Aqua Link Base Network — plus occasional safety tips.',
        placeholder: '(555) 123-4567',
        button: 'Text me my 10% code',
        done: 'Check your texts — we just sent your one-time 10% off code. Enter it at checkout to save on your Base Network.',
        disclaimer: 'By providing your number, you agree to receive recurring automated marketing texts from Aqua Link at the number provided. Consent is not a condition of purchase. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
    } : {
        blurb: 'Join our email list and we\'ll send you a code for 10% off the Aqua Link Base Network — plus occasional safety tips.',
        placeholder: 'you@email.com',
        button: 'Send my 10% discount',
        done: 'Check your inbox — we just sent your one-time 10% off code. Enter it at checkout to save on your Base Network.',
        disclaimer: 'By submitting, you agree to receive marketing emails from Aqua Link at the address provided. Consent is not a condition of purchase. Unsubscribe anytime.',
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                animation: 'fadeIn 0.28s ease',
            }}>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 440,
                    backgroundColor: Appearance.colors.white,
                    borderRadius: 24,
                    boxShadow: Appearance.boxShadow.cardHover,
                    padding: Utils.isMobile() ? '36px 26px' : '44px 40px',
                    textAlign: 'center',
                    animation: 'scaleIn 0.3s ease both',
                }}>

                {/* Close */}
                <div
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: 16, right: 16,
                        width: 32, height: 32, borderRadius: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: Appearance.colors.subText(),
                    }}>
                    <i className={'fas fa-times'} style={{ fontSize: 16 }} />
                </div>

                {status === 'done' ? (
                    <>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            backgroundColor: Utils.hexToRGBA(Appearance.colors.success, 0.15),
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
                        }}>
                            <i className={'fas fa-check'} style={{ fontSize: 22, color: Appearance.colors.success }} />
                        </div>
                        <h3 style={{ ...Appearance.textStyles.heading({ fontSize: 24 }), marginBottom: 10 }}>{'You\'re in!'}</h3>
                        <p style={Appearance.textStyles.body()}>
                            {copy.done}
                        </p>
                    </>
                ) : (
                    <>
                        <img
                        src={'/images/splash-icon.png'}
                        alt={'Aqua Link'}
                        style={{
                            width: 56, height: 56, borderRadius: 28,
                            backgroundColor: Appearance.colors.primary(),
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
                        }} />
                        <h3 style={{ ...Appearance.textStyles.heading({ fontSize: Utils.isMobile() ? 24 : 28 }), marginBottom: 10 }}>
                            {'Get 10% Off'}
                        </h3>
                        <p style={{ ...Appearance.textStyles.body(), marginBottom: 22 }}>
                            {copy.blurb}
                        </p>

                        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <input
                                type={isSms ? 'tel' : 'email'}
                                inputMode={isSms ? 'tel' : 'email'}
                                autoComplete={isSms ? 'tel' : 'email'}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder={copy.placeholder}
                                required={true}
                                style={fieldStyle} />

                            {error && (
                                <span style={Appearance.textStyles.caption({ color: Appearance.colors.error })}>{error}</span>
                            )}

                            <Button
                                label={copy.button}
                                type={'primary'}
                                loading={status === 'submitting'}
                                onClick={() => {}}
                                style={{ padding: '14px 24px', fontSize: 16, width: '100%' }} />
                        </form>

                        {/* Marketing-consent disclaimer */}
                        <p style={{
                            ...Appearance.textStyles.caption({ color: Appearance.colors.lightGrey }),
                            fontSize: 11,
                            lineHeight: 1.45,
                            marginTop: 14,
                            textAlign: 'center',
                        }}>
                            {copy.disclaimer}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default OutreachCaptureModal;
